import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import Button from '../components/Button'
import LoadingSpinner from '../components/LoadingSpinner'
import api from '../services/api'
import { trackEvent } from '../utils/mixpanel'
import { formatSessionDate, formatSessionTime } from '../utils/helpers'
import { getMediaUrl } from '../utils/mediaHelpers'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

const getEventImage = (event) => {
  if (event.image) {
    if (event.image.startsWith('http')) return event.image
    return `${API_BASE_URL}${event.image}`
  }
  if (event.media?.length > 0) {
    const firstImageMedia = event.media.find((m) => m.type === 'image')
    if (firstImageMedia) return getMediaUrl(firstImageMedia.url)
  }
  return `${API_BASE_URL}/uploads/events/default-event.jpg`
}

const eventDetailPath = (event) =>
  event.slug ? `/events/${encodeURIComponent(event.slug)}` : '/events'

const FacilitatorSlugEvents = () => {
  const { slug } = useParams()
  const [facilitator, setFacilitator] = useState(null)
  const [events, setEvents] = useState({ upcoming: [], past: [] })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    trackEvent('Page Viewed', { page: 'Facilitator Events', slug })
  }, [slug])

  useEffect(() => {
    if (!slug) return

    const load = async () => {
      try {
        setLoading(true)
        setError(null)
        const [profileRes, eventsRes] = await Promise.all([
          api.get(`/api/facilitators/profile/${slug}`),
          api.get(`/api/facilitators/profile/${slug}/events`),
        ])
        if (profileRes.data.success) {
          setFacilitator(profileRes.data.data)
        }
        if (eventsRes.data.success) {
          setEvents(eventsRes.data.data)
        }
      } catch (err) {
        console.error('Error loading facilitator events page:', err)
        if (err.response?.status === 404) {
          setError('Facilitator not found')
        } else {
          setError('Failed to load events')
        }
        setFacilitator(null)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [slug])

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !facilitator) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-4">
            {error || 'Facilitator not found'}
          </h1>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            The facilitator you&apos;re looking for doesn&apos;t exist or has been removed.
          </p>
          <Link to="/facilitators">
            <Button variant="primary">View All Facilitators</Button>
          </Link>
        </div>
      </div>
    )
  }

  const hasAnyEvents = events.upcoming.length > 0 || events.past.length > 0

  return (
    <div className="bg-cream min-h-screen">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="pt-16 sm:pt-20 pb-8 sm:pb-12 border-b border-gray-200/80 bg-gradient-to-b from-cream via-cream-50 to-cream"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <nav className="text-sm text-gray-600 mb-4 flex flex-wrap items-center gap-2">
              <Link to="/facilitators" className="hover:text-primary-600 transition-colors">
                Facilitators
              </Link>
              <span aria-hidden="true">/</span>
              <Link to={`/facilitator/${slug}`} className="hover:text-primary-600 transition-colors truncate max-w-[40vw] sm:max-w-none">
                {facilitator.name}
              </Link>
              <span aria-hidden="true">/</span>
              <span className="text-gray-900 font-medium">Events</span>
            </nav>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
              {facilitator.name}
            </h1>
            <p className="mt-2 text-lg text-primary-600 font-medium">Events</p>
            <Link
              to={`/facilitator/${slug}`}
              className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-primary-600 hover:text-primary-700"
            >
              <svg className="w-4 h-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Full profile
            </Link>
          </div>
        </div>
      </motion.div>

      <div className="container mx-auto px-4 py-8 sm:py-12">
        <div className="max-w-6xl mx-auto space-y-10 sm:space-y-14">
          {!hasAnyEvents && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 sm:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-white/50 text-center"
            >
              <p className="text-gray-700 text-lg mb-6">
                No published events yet for this facilitator.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to={`/facilitator/${slug}`}>
                  <Button variant="secondary">View profile</Button>
                </Link>
                <Link to="/events">
                  <Button variant="primary">Browse all events</Button>
                </Link>
              </div>
            </motion.div>
          )}

          {events.upcoming.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="relative"
            >
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 sm:p-10 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-white/50">
                <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-6 sm:mb-8 tracking-tight flex items-center gap-3">
                  <span className="text-2xl sm:text-3xl" aria-hidden="true">
                    📅
                  </span>
                  Upcoming Events
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-1 lg:gap-10">
                  {events.upcoming.map((event) => (
                    <Link key={event.id} to={eventDetailPath(event)} className="block group">
                      <div className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 h-full">
                        <div className="aspect-[4/5] overflow-hidden bg-gray-200">
                          <img
                            src={getEventImage(event)}
                            alt={event.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              e.target.src = '/uploads/events/default-event.jpg'
                            }}
                          />
                        </div>
                        <div className="p-5">
                          <div className="flex flex-wrap gap-2 mb-3">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                event.format === 'online'
                                  ? 'bg-blue-100 text-blue-800'
                                  : event.format === 'in-person'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-purple-100 text-purple-800'
                              }`}
                            >
                              {event.format}
                            </span>
                            {event.fee?.toLowerCase() === 'free' && (
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                                FREE
                              </span>
                            )}
                          </div>
                          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                            {event.name}
                          </h3>
                          <div className="flex items-center gap-2 text-gray-600 text-sm mb-2">
                            <span aria-hidden="true">📅</span>
                            <span>{formatSessionDate(event.eventDate)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600 text-sm">
                            <span aria-hidden="true">⏰</span>
                            <span>
                              {formatSessionTime(event.eventDate, event.endDate)}
                            </span>
                          </div>
                          {(event.space || event.location) && (
                            <div className="flex items-center gap-2 text-gray-600 text-sm mt-2">
                              <span aria-hidden="true">📍</span>
                              <span className="line-clamp-2">
                                {event.space
                                  ? `${event.space.name}${event.space.city ? `, ${event.space.city}` : ''}`
                                  : event.location}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                <div className="mt-6 text-center">
                  <Link
                    to="/events"
                    className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-semibold text-sm sm:text-base"
                  >
                    View all events on Wellness Loop
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}

          {events.past.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="relative"
            >
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 sm:p-10 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-white/50">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 tracking-tight flex items-center gap-3">
                  <span className="text-2xl" aria-hidden="true">
                    🗓️
                  </span>
                  Past Events
                </h2>
                <div className="space-y-3 sm:space-y-4">
                  {events.past.map((event) => (
                    <Link
                      key={event.id}
                      to={eventDetailPath(event)}
                      className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group"
                    >
                      <div className="w-12 sm:w-14 aspect-[4/5] rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                        <img
                          src={getEventImage(event)}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = '/uploads/events/default-event.jpg'
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate group-hover:text-primary-600 transition-colors">
                          {event.name}
                        </h3>
                        <p className="text-sm text-gray-500">{formatSessionDate(event.eventDate)}</p>
                      </div>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                          event.format === 'online'
                            ? 'bg-blue-100 text-blue-700'
                            : event.format === 'in-person'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-purple-100 text-purple-700'
                        }`}
                      >
                        {event.format}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FacilitatorSlugEvents
